/**
 * `LineChart` — spec §F2 `LineChart(series[{name,short,color,values}], {unit})`:
 * "gridlines #EEF1EC, y-ticks, direct end-labels, hover dots with tooltip".
 *
 * The mockup's `lineChart()`, with the three changes real data forces — the same three
 * `Spark` already made, for the same reasons:
 *
 * 1. **Nulls break the line.** §H1's `series[].values` carries `null` for a year a unit did
 *    not report, and a polyline drawn straight across the gap is a fabricated trend. The
 *    series is split into runs of consecutive present points; an isolated point is drawn as
 *    a dot with no line.
 * 2. **The end label goes on the last point that exists**, not on `values[values.length-1]`
 *    — which for a series ending in a gap is `null`, and `Y(null)` is `NaN`, i.e. a label
 *    that silently disappears from the SVG.
 * 3. **Hover through the shared `ChartTip` portal** (§F2), not the mockup's `data-tip` pass.
 *
 * Every number on screen is an ink token, never the series colour (§F2/§G8); the colour is
 * carried by the line, the dot ring and the direct label, which is a *name* and not a value.
 *
 * The right margin is the direct labels' gutter: §G8 requires them for ≥2 series, and a
 * label drawn past the viewBox is the same as no label at all.
 */
import { useId, useMemo } from 'react';

import { fmtCompact, fmtNum } from '../../lib/format';
import { hideChartTip, showChartTip } from './chartTipStore';

/** The mockup's `padL/padR/padT/padB`, plus the label gutter its viewBox added (`w + 34`). */
export const LINE_PAD_LEFT = 44;
export const LINE_PAD_RIGHT = 14;
export const LINE_PAD_TOP = 14;
export const LINE_PAD_BOTTOM = 26;
export const LINE_LABEL_GUTTER = 34;
/** Mockup: five horizontal rules (`ticks = 4`, drawn inclusive of both ends). */
export const LINE_TICKS = 4;
/** §F2's gridline ink. */
export const GRID_INK = '#EEF1EC';
const AXIS_INK = '#8B958A';
/**
 * Default for `zeroBaseline`: draw from zero unless the series' floor is above this share of
 * its peak.
 *
 * **0.4, the mockup's own threshold** — every line chart in the app shares this component,
 * and a higher bar flattened the Overview's trajectory and the Compare tool onto a zero
 * axis they were never drawn against (review, 2026-09-16). Below it a zero baseline is the
 * honest frame; above it (a 95-100 % adoption rate, say) zero would press the whole series
 * into one line at the top of the frame and the chart would say nothing.
 */
export const ZERO_BASELINE = 0.4;
/**
 * The least space that must stand between the highest point and the top gridline, as a
 * share of the axis. Below it the axis takes one more step: an axis rounded from the data
 * can land exactly on the peak, and a line drawn along its own top gridline reads as a
 * clipped chart (review, 2026-09-16).
 */
const TOP_CLEARANCE = 0.03;
/** Minimum vertical gap between two direct end labels before they are pushed apart. */
const LINE_LABEL_MIN_GAP = 11;
/**
 * How far past the plot's right edge a direct label starts — clear of the last dot's ring,
 * and no further: the gutter is `LINE_LABEL_GUTTER` wide and every pixel spent here is a
 * pixel a long label ("Achieved") loses to the frame.
 */
const LINE_END_OFFSET = 5;

/**
 * The **smallest** 1, 2 or 5 times a power of ten that fits `lo`..`hi` into `LINE_TICKS`
 * intervals — the tightest round axis the chart can carry.
 *
 * Dividing the span by the tick count and rounding that up overshoots: a 5,865 peak asks
 * for a 1,583 step, rounds to 2,000, and then the axis top rounds up again to 8,000 — a
 * third of the frame left empty above the line (review, 2026-09-16). Walking the ladder
 * from the bottom and stopping at the first step that *fits* gives 0-6,000 instead.
 */
function niceStep(lo: number, hi: number, intervals: number): number {
  const span = hi - lo;
  if (!Number.isFinite(span) || span <= 0) return 1;
  const start = Math.floor(Math.log10(span / Math.max(intervals, 1))) - 1;
  for (let exp = start; exp < start + 6; exp += 1) {
    for (const factor of [1, 2, 5]) {
      const step = factor * 10 ** exp;
      if (step <= 0) continue;
      if (Math.ceil(hi / step) - Math.floor(lo / step) <= intervals) return step;
    }
  }
  return span / intervals;
}

/**
 * Keep the direct end labels legible: away from the last point, and off each other.
 *
 * The labels sit in the right gutter at their series' last y. Two countries a few hundred
 * producers apart put two names on the same line of pixels, and the label started two pixels
 * after the last dot, overprinting its ring (client, 2026-09-16 round 2). This nudges any
 * label closer than `LINE_LABEL_MIN_GAP` to its neighbour downwards, in y order, then pulls
 * the whole run back up if it has overrun the frame — so the order still matches the lines.
 */
function spreadLabels(ys: readonly number[], top: number, bottom: number): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  let previous = -Infinity;
  for (const entry of order) {
    entry.y = Math.max(entry.y, previous + LINE_LABEL_MIN_GAP);
    previous = entry.y;
  }
  const overrun = (order[order.length - 1]?.y ?? 0) - bottom;
  if (overrun > 0) {
    // …and if that pushes the top one off the frame, the labels simply cannot all be
    // separated: clamp and accept one overlap rather than drawing outside the chart.
    let floor = top;
    for (const entry of order) {
      entry.y = Math.max(entry.y - overrun, floor);
      floor = entry.y;
    }
  }
  const out = [...ys];
  for (const entry of order) out[entry.i] = entry.y;
  return out;
}

export interface LineSeries {
  name: string;
  /** The direct end label; falls back to `name`. */
  short?: string;
  color: string;
  /** One per `labels` entry; `null`/non-finite = no data for that period. */
  values: readonly (number | null | undefined)[];
}

export interface LineChartProps {
  series: readonly LineSeries[];
  /** X categories — normally the §F1 years. */
  labels: readonly (string | number)[];
  w?: number;
  h?: number;
  /** Appended to the tooltip value. */
  unit?: string;
  /** §G3 decimals for the tooltip; the y ticks stay whole numbers, as in the mockup. */
  fmt?: number;
  ariaLabel?: string;
  className?: string;
  /**
   * How far above zero the series' floor may sit before the axis starts at that floor
   * instead of at zero, as a share of the peak. `ZERO_BASELINE` by default.
   *
   * `1` means **always start at zero**, whatever the floor — the dashboard's trend card
   * asks for that (approved design): its reader is comparing a country's absolute progress
   * against a programme target, and an axis that starts at 14,000 makes a 5 % climb look
   * like a doubling. A chart of rates or shares wants the default instead.
   */
  zeroBaseline?: number;
  /**
   * A faint area under each line, from 10 % of the series' colour at the line to nothing at
   * the axis (client, 2026-09-16: "faint colours, a modern look").
   *
   * Off by default and opt-in per caller: the Compare tool and the insights blocks draw
   * several series over one another, where stacked translucent washes muddy every colour
   * they overlap. The dashboard's trend card, whose lines rarely cross, asks for it.
   *
   * It is decoration over the same geometry — one polygon per already-computed run, closed
   * onto the axis floor — so a gap in a series still breaks both the line and its wash.
   */
  areaFill?: boolean;
}

interface Point {
  i: number;
  v: number;
  x: number;
  y: number;
}

/** Consecutive present points — the only segments that may legitimately be joined. */
function runsOf(points: readonly Point[]): Point[][] {
  const runs: Point[][] = [];
  let current: Point[] = [];
  for (const p of points) {
    const last = current[current.length - 1];
    if (last && p.i !== last.i + 1) {
      runs.push(current);
      current = [];
    }
    current.push(p);
  }
  if (current.length) runs.push(current);
  return runs;
}

export function LineChart({
  series,
  labels,
  w = 330,
  h = 190,
  unit = '',
  fmt = 2,
  ariaLabel,
  className,
  zeroBaseline = ZERO_BASELINE,
  areaFill = false,
}: LineChartProps) {
  /* One gradient per series, ids kept unique across charts sharing a page. */
  const rawId = useId();
  const gradPrefix = `lc${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const chart = useMemo(() => {
    const n = Math.max(labels.length, 1);
    const numbers = series.map((s) =>
      labels.map((_, i) => {
        const v = s.values[i];
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
      }),
    );
    const present = numbers.flat().filter((v): v is number => v !== null);
    const empty = present.length === 0;

    /*
      **A round axis** (client, 2026-09-16 round 2). The axis used to divide the data's own
      span into four, which printed ticks like "14,702 / 19,580 / 24,458" — five numbers a
      reader has to decode before reading the chart, and on a flat series the same number
      five times over. Instead:

      * the baseline is **zero** unless the series' floor is far above it (`ZERO_BASELINE`),
        because a trend drawn from its own floor exaggerates every move;
      * there is 8 % headroom over the maximum, so a line — a flat one included — never runs
        along the top gridline;
      * the step is the nearest 1, 2 or 5 times a power of ten, and both ends are snapped to
        multiples of it. That is what turns 14,702-34,213 into 0 · 10,000 · … · 40,000.

      An **empty** series keeps the old evenly-divided 0-1 axis: there is nothing to round to,
      and a chart with no scale at all reads as a broken one.
    */
    let min = empty ? 0 : Math.min(...present);
    let max = empty ? 1 : Math.max(...present);
    let step = 1;
    if (!empty) {
      const peak = max;
      if (min > 0 && min < max * zeroBaseline) min = 0;
      if (min === max) min = Math.min(0, min);
      // The 8 % headroom is what picks the step — it keeps the axis from being chosen so
      // tightly that the line lands on the top gridline…
      const padded = peak + (peak - min || Math.abs(peak) || 1) * 0.08;
      step = niceStep(min, padded, LINE_TICKS);
      min = Math.floor(min / step) * step;
      // …but the top itself is rounded from the *data*, so an axis that already clears the
      // peak by a visible margin is not pushed up another whole step for nothing. One more
      // step only where the line would otherwise run along the top gridline.
      max = Math.ceil(peak / step) * step;
      if (max - peak < (max - min) * TOP_CLEARANCE) max += step;
    }
    const range = max - min || 1;

    const X = (i: number) => LINE_PAD_LEFT + (n > 1 ? (i * (w - LINE_PAD_LEFT - LINE_PAD_RIGHT)) / (n - 1) : 0);
    const Y = (v: number) =>
      LINE_PAD_TOP + (1 - (v - min) / range) * (h - LINE_PAD_TOP - LINE_PAD_BOTTOM);

    const tickValues = empty
      ? Array.from({ length: LINE_TICKS + 1 }, (_, i) => min + (i * range) / LINE_TICKS)
      : (() => {
          const out: number[] = [];
          // `+ step / 2` rather than `<= max`: both ends are exact multiples of the step,
          // but repeated addition of a decimal step drifts, and the last tick must not be
          // lost to a floating-point crumb.
          for (let v = min; v <= max + step / 2 && out.length < 12; v += step) {
            out.push(Math.abs(v) < step / 1e6 ? 0 : v);
          }
          return out;
        })();
    const ticks = tickValues.map((v) => ({ v, y: Y(v) }));

    const drawn = series.map((s, si) => {
      const points: Point[] = [];
      (numbers[si] ?? []).forEach((v, i) => {
        if (v !== null) points.push({ i, v, x: X(i), y: Y(v) });
      });
      return { series: s, points, runs: runsOf(points), last: points[points.length - 1] };
    });

    /*
      The direct end labels, nudged off each other and off the last dot they name. The x
      offset clears the dot's own radius and ring (`LINE_END_OFFSET`); `spreadLabels` owns
      the vertical separation, and it is given every series' last y at once because pushing
      one label down is only correct in the context of the others.
    */
    const labelYs = spreadLabels(
      drawn.map((line) => (line.last ? line.last.y + 3.5 : 0)),
      LINE_PAD_TOP + 4,
      h - LINE_PAD_BOTTOM,
    );
    const lines = drawn.map((line, si) => ({
      series: line.series,
      points: line.points,
      runs: line.runs,
      end: line.last ? { x: w - LINE_PAD_RIGHT + LINE_END_OFFSET, y: labelYs[si] ?? line.last.y } : null,
    }));

    return { ticks, lines, X, baseY: h - LINE_PAD_BOTTOM };
  }, [series, labels, w, h, zeroBaseline]);

  const tipValue = (v: number) => `${fmtNum(v, fmt)}${unit ? ` ${unit}` : ''}`;

  return (
    <svg
      className={`chart linechart${className ? ` ${className}` : ''}`}
      width="100%"
      viewBox={`0 0 ${w + LINE_LABEL_GUTTER} ${h}`}
      role="img"
      aria-label={ariaLabel ?? `${series.length} series`}
      data-series={series.length}
      onMouseLeave={hideChartTip}
    >
      {areaFill ? (
        <defs>
          {series.map((s, si) => (
            <linearGradient key={`grad-${si}`} id={`${gradPrefix}-${si}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.1} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
      ) : null}

      {chart.ticks.map((tick, i) => (
        <g key={`tick-${i}`}>
          <line
            x1={LINE_PAD_LEFT}
            x2={w - LINE_PAD_RIGHT}
            y1={tick.y}
            y2={tick.y}
            stroke={GRID_INK}
            strokeWidth={1}
            data-gridline={i}
          />
          <text
            x={LINE_PAD_LEFT - 7}
            y={tick.y + 3.5}
            textAnchor="end"
            fontSize={9.5}
            fill={AXIS_INK}
            data-ytick={i}
          >
            {/* Six-digit ticks ("1,620,000") overrun the 44px gutter the mockup sized for
                five; from 100 000 up the axis reads compact ("1.6M"), as `BarsH`'s does. */}
            {Math.abs(chart.ticks[chart.ticks.length - 1]?.v ?? 0) >= 100_000
              ? fmtCompact(tick.v)
              : fmtNum(tick.v, 0)}
          </text>
        </g>
      ))}

      {labels.map((label, i) => (
        <text
          key={`x-${label}-${i}`}
          x={chart.X(i)}
          y={h - 8}
          textAnchor="middle"
          fontSize={9.5}
          fill={AXIS_INK}
          data-xtick={i}
        >
          {label}
        </text>
      ))}

      {chart.lines.map((line, si) => (
        <g key={`${line.series.name}-${si}`} data-line={si}>
          {areaFill
            ? line.runs.map((run, ri) =>
                run.length > 1 ? (
                  <polygon
                    key={`area-${ri}`}
                    data-line-area={si}
                    points={`${run[0]?.x},${chart.baseY} ${run
                      .map((p) => `${p.x},${p.y}`)
                      .join(' ')} ${run[run.length - 1]?.x},${chart.baseY}`}
                    fill={`url(#${gradPrefix}-${si})`}
                    stroke="none"
                    pointerEvents="none"
                  />
                ) : null,
              )
            : null}
          {line.runs.map((run, ri) =>
            run.length > 1 ? (
              <polyline
                key={`run-${ri}`}
                points={run.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={line.series.color}
                strokeWidth={2}
                strokeLinejoin="round"
              />
            ) : null,
          )}
          {line.points.map((p) => (
            <g key={`p-${p.i}`}>
              <circle
                cx={p.x}
                cy={p.y}
                r={7}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                data-dot={`${si}-${p.i}`}
                data-value={tipValue(p.v)}
                onMouseEnter={(e) =>
                  showChartTip(e, `${line.series.name} · ${labels[p.i]}`, tipValue(p.v))
                }
                onMouseMove={(e) =>
                  showChartTip(e, `${line.series.name} · ${labels[p.i]}`, tipValue(p.v))
                }
                onMouseLeave={hideChartTip}
              />
              <circle
                cx={p.x}
                cy={p.y}
                r={2.6}
                fill="#fff"
                stroke={line.series.color}
                strokeWidth={1.8}
                pointerEvents="none"
              />
            </g>
          ))}
          {line.end ? (
            <text
              x={line.end.x}
              y={line.end.y}
              fontSize={9.8}
              fontWeight={700}
              fill={line.series.color}
              data-endlabel={si}
            >
              {line.series.short ?? line.series.name}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}

export default LineChart;
